import React, { useState } from 'react'
import {
    Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, TextField, Tooltip
} from '@mui/material'
import { DataGridPro, ptBR } from '@mui/x-data-grid-pro'
import LoadingButton from '@mui/lab/LoadingButton'
import CloseIcon from '@mui/icons-material/Close'
import HelpIcon from '@mui/icons-material/Help'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import DeleteIcon from '@mui/icons-material/Delete'
import UploadIcon from '@mui/icons-material/Upload'
import { read, utils, writeFile } from 'xlsx'

import api from '../../../../api'
import useNotification from '../../../Notification'
import { decodeImportedText, normalizeImportHeader } from '../../../Crm/TabButtons/import-utils'

const fields = [
    { field: 'identificacao', label: 'Identificação', width: 130 },
    { field: 'mensagem', label: 'Mensagem', flex: 2, minWidth: 250 },
    { field: 'categoria', label: 'Categoria', flex: 1, minWidth: 150 },
    { field: 'fila', label: 'Fila', flex: 1, minWidth: 180 },
    { field: 'tipo', label: 'Tipo', flex: 1, minWidth: 180 }
]
const identificationHelp = 'Deixe a identificação vazia para criar uma nova mensagem. Informe o ID exportado para atualizar uma mensagem existente do mesmo tipo. Um ID inexistente retorna erro.'

const ImportMessages = ({ enterpriseId, handleClose, onImported }) => {
    const [rows, setRows] = useState([])
    const [errors, setErrors] = useState([])
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [validated, setValidated] = useState(false)
    const [pageSize, setPageSize] = useState(25)

    const validateRows = async (data, validateOnly = true) => {
        setLoading(true)
        setError('')
        setValidated(false)
        try {
            const response = await api.post(`/api/enterprises/${enterpriseId}/predefinedmessages/import/`, {
                rows: data.map(({ id, line, ...row }) => row),
                validate_only: validateOnly
            }, { timeout: 0 })
            if (response?.status !== 200) {
                const rowErrors = response?.data?.errors
                setErrors(Array.isArray(rowErrors) ? rowErrors : [])
                setError(Array.isArray(rowErrors)
                    ? 'Corrija os erros indicados abaixo e valide novamente. Nenhuma mensagem foi importada.'
                    : response?.data?.detail || 'Não foi possível validar o arquivo. Verifique os dados e tente novamente.')
                setValidated(false)
                return
            }
            setErrors([])
            setValidated(true)
            if (!validateOnly) {
                useNotification('Sucesso!', `${response.data.created} mensagens criadas e ${response.data.updated} atualizadas.`, 'success')
                handleClose()
                try {
                    await onImported()
                } catch (err) {
                    useNotification('Atenção!', 'Mensagens importadas. Recarregue a tela para atualizar a lista.', 'warning')
                }
            }
        } catch (err) {
            setValidated(false)
            setError('Não foi possível concluir a importação. Verifique a conexão e tente novamente.')
        } finally {
            setLoading(false)
        }
    }

    const prepareData = (file) => {
        if (!file) return
        setLoading(true)
        setError('')
        setRows([])
        setErrors([])
        setValidated(false)
        const reader = new FileReader()
        reader.onerror = () => {
            setError('Não foi possível ler o arquivo selecionado.')
            setLoading(false)
        }
        reader.onload = async (event) => {
            try {
                const workbook = /\.csv$/i.test(file.name)
                    ? read(decodeImportedText(event.target.result), { type: 'string', raw: true })
                    : read(event.target.result, { type: 'array', raw: true })
                const sheet = workbook.Sheets[workbook.SheetNames[0]]
                if (!sheet) throw new Error('O arquivo não contém uma planilha.')
                const data = utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false, blankrows: true })
                const header = (data.shift() || []).map(value => normalizeImportHeader(String(value)))
                if (header.length !== fields.length || fields.some((field, index) => header[index] !== field.field)) {
                    throw new Error('As colunas devem ser: Identificação, Mensagem, Categoria, Fila e Tipo, nesta ordem. Baixe o modelo para preencher.')
                }
                const loaded = data.map((values, index) => ({
                    id: index,
                    line: index + 2,
                    ...Object.fromEntries(fields.map((field, position) => [field.field, String(values[position] ?? '')]))
                })).filter(row => fields.some(field => row[field.field].trim()))
                if (!loaded.length) throw new Error('O arquivo não contém mensagens para importar.')
                if (loaded.length > 5000) throw new Error('Importe no máximo 5000 mensagens por arquivo.')
                setRows(loaded)
                await validateRows(loaded)
            } catch (err) {
                setError(err.message || 'Arquivo inválido. Selecione um arquivo CSV, XLS ou XLSX.')
            } finally {
                setLoading(false)
            }
        }
        reader.readAsArrayBuffer(file)
    }

    const updateRow = (id, field, value) => {
        setRows(current => current.map(row => row.id === id ? { ...row, [field]: value } : row))
        setValidated(false)
    }

    const downloadTemplate = () => {
        const workbook = utils.book_new()
        utils.book_append_sheet(workbook, utils.aoa_to_sheet([fields.map(field => field.label)]), 'Mensagens predefinidas')
        writeFile(workbook, 'modelo_mensagens_predefinidas.xlsx')
    }

    const columns = [
        ...fields.map(({ label, ...field }) => ({
            ...field,
            headerName: label,
            sortable: false,
            renderHeader: field.field === 'identificacao' ? () => (
                <Box display="flex" alignItems="center" gap={0.5}>
                    Identificação
                    <Tooltip title={identificationHelp}><HelpIcon fontSize="small" /></Tooltip>
                </Box>
            ) : undefined,
            renderCell: (params) => {
                const index = rows.findIndex(row => row.id === params.id)
                const fieldErrors = errors[index]?.[field.field]
                return (
                    <Tooltip title={fieldErrors?.join(' ') || ''}>
                        <TextField
                            fullWidth
                            size="small"
                            multiline={field.field === 'mensagem'}
                            maxRows={3}
                            value={params.value}
                            error={!!fieldErrors}
                            disabled={loading}
                            inputProps={{ 'aria-label': label }}
                            onKeyDown={(event) => event.stopPropagation()}
                            onChange={(event) => updateRow(params.id, field.field, event.target.value)}
                        />
                    </Tooltip>
                )
            }
        })),
        { field: 'line', headerName: 'Linha', width: 70 },
        {
            field: 'errors', headerName: 'Erros', width: 115, sortable: false,
            renderCell: (params) => {
                const rowErrors = Object.values(errors[rows.findIndex(row => row.id === params.id)] || {}).flat()
                if (!rowErrors.length) return null
                return (
                    <Tooltip arrow title={
                        <Box component="ul" sx={{ m: 0, pl: 2, overflowWrap: 'anywhere' }}>
                            {rowErrors.map((message, index) => <li key={index}>{message}</li>)}
                        </Box>
                    }>
                        <Box component="span" tabIndex={0} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'error.main', cursor: 'help', whiteSpace: 'nowrap' }}>
                            <ErrorOutlineIcon fontSize="small" />
                            {rowErrors.length} {rowErrors.length === 1 ? 'erro' : 'erros'}
                        </Box>
                    </Tooltip>
                )
            }
        },
        {
            field: 'actions', headerName: '', width: 60, align: 'center', sortable: false,
            renderCell: (params) => (
                <IconButton aria-label="Remover linha" size="small" color="error" disabled={loading} onClick={async () => {
                    const index = rows.findIndex(row => row.id === params.id)
                    const remainingRows = rows.filter(row => row.id !== params.id)
                    setRows(remainingRows)
                    setErrors(current => current.filter((row, position) => position !== index))
                    setValidated(false)
                    setError('')
                    if (remainingRows.length) await validateRows(remainingRows)
                }}><DeleteIcon /></IconButton>
            )
        }
    ]

    return (
        <Dialog open fullWidth maxWidth="xl" disableEnforceFocus onClose={loading ? undefined : handleClose}>
            <DialogTitle className="group-flexbox">
                <span>Importar mensagens predefinidas</span>
                <IconButton aria-label="Fechar" disabled={loading} onClick={handleClose}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Alert severity="info" sx={{ mb: 2 }}>
                    Use CSV, XLS ou XLSX, com até 5000 mensagens. {identificationHelp}
                    {' '}Categoria e fila devem existir. Para todas as filas, use “Qualquer Fila”; para várias filas, separe os nomes por ponto e vírgula (;).
                    {' '}Tipo: “Geral” ou “Operador: nome”. Novas mensagens serão de texto; mensagens HTML atualizadas por ID manterão seu formato e título.
                </Alert>
                <Box display="flex" gap={1} mb={2}>
                    <Button component="label" startIcon={<UploadIcon />} disabled={loading}>
                        Selecionar arquivo
                        <input hidden type="file" accept=".csv,.xls,.xlsx" onChange={(event) => {
                            prepareData(event.target.files[0])
                            event.target.value = null
                        }} />
                    </Button>
                    <Button onClick={downloadTemplate}>Baixar modelo</Button>
                </Box>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {rows.length > 0 && <>
                    <Box sx={{ mb: 1 }}>
                        Mensagens: {rows.length} | Com erros: {errors.filter(row => Object.keys(row).length).length}
                    </Box>
                    <Box sx={{ height: 460 }}>
                        <DataGridPro
                            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
                            rows={rows}
                            columns={columns}
                            rowHeight={100}
                            disableSelectionOnClick
                            disableColumnMenu
                            loading={loading}
                            pagination
                            pageSize={pageSize}
                            onPageSizeChange={setPageSize}
                            rowsPerPageOptions={[25, 50, 100]}
                        />
                    </Box>
                </>}
                {validated && <Alert severity="success" sx={{ mt: 2 }}>Dados válidos. Confirme para importar as mensagens.</Alert>}
            </DialogContent>
            <DialogActions>
                <Button disabled={loading} onClick={handleClose}>Cancelar</Button>
                <LoadingButton loading={loading} disabled={!rows.length || loading} onClick={() => validateRows(rows)}>Validar</LoadingButton>
                <LoadingButton loading={loading} disabled={!validated || loading} onClick={() => validateRows(rows, false)}>Importar</LoadingButton>
            </DialogActions>
        </Dialog>
    )
}

export default ImportMessages
